import { useMemo, useState } from "react";
import {
  Badge,
  BetaMenuTriggerItem,
  BetaMenuTriggerList,
  BetaMenuTriggerPopover,
  BetaMenuTriggerRoot,
  Button,
  ServiceIcon,
  ServiceIconShell,
  Text,
} from "@zapier/design-system";
import { Icon } from "@zapier/zinnia-icons";
import { useTranslations } from "@zapier/i18n";
import {
  AssetTypes,
  getAssetIcon,
  getEditZapUrl,
  getServiceIconUrl,
  useNotification,
} from "@zapier/utils";
import {
  type HydratedApp,
  useConnections,
  useRefetchAuthentications,
  useWireConnections,
} from "@zapier/client";
import type { CoveredZap } from "@zapier/bff";

import useCreateAppConnection from "../useCreateAppConnection";
import {
  type AppConnectionGroup,
  compatKey,
  versionless,
} from "./connectionGroups";
import { TruncatedText } from "./TruncatedText";
import styles from "./ConnectAppsModal.module.css";

/** How a row ended up resolved — drives confirm-on-close + delete-on-close. */
export interface RowResolution {
  /** The auth the row is wired to. */
  authId: number;
  /**
   * True when the user reconnected the row's own placeholder (via "Connect"),
   * as opposed to switching to a different existing auth. Reconnected (and
   * untouched) placeholders are kept on close; switched-away ones are deleted.
   */
  reconnectedPlaceholder: boolean;
}

interface Props {
  group: AppConnectionGroup;
  isWired: boolean;
  onWired: (key: string, resolution: RowResolution) => void;
  /**
   * Short version tag (e.g. `v2`) appended to the app name to tell apart two
   * legacy rows that resolve to the same app name. Only set on a name collision.
   */
  disambiguator?: string;
}

/** Collapsible list of the copied Zaps a connection covers (placeholder rows). */
function ZapsAccordion({ zaps }: { zaps: CoveredZap[] }) {
  const t = useTranslations("connect-apps-modal");
  const [isOpen, setIsOpen] = useState(false);
  if (zaps.length === 0) return;
  return (
    <div className={styles.accordion}>
      <button
        type="button"
        className={styles.accordionHeader}
        onClick={() => setIsOpen((open) => !open)}
        aria-expanded={isOpen}
      >
        <span className={styles.accordionHeaderLabel}>
          <Icon
            name={isOpen ? "arrowSmallDown" : "arrowSmallRight"}
            size={24}
          />
          <Text
            type="Body2"
            tag="span"
            color="TextDefault"
            className={styles.accordionTitle}
          >
            {t("row.zaps-accordion")}
          </Text>
        </span>
        <Badge variant="neutral">{String(zaps.length)}</Badge>
      </button>
      {isOpen ? (
        <div className={styles.accordionList}>
          {zaps.map((zap) => (
            <a
              key={zap.id}
              className={styles.zapRow}
              href={getEditZapUrl(zap.id)}
              target="_blank"
              rel="noreferrer"
              title={t("row.open-zap")}
            >
              <ServiceIconShell size="small">
                <Icon
                  name={getAssetIcon(AssetTypes.Zap)}
                  size={16}
                  color="BrandOrange"
                />
              </ServiceIconShell>
              <Text
                type="SmallPrint1"
                tag="span"
                color="TextDefault"
                className={styles.zapRowTitle}
              >
                {zap.title}
              </Text>
              <span className={styles.zapRowExternal} aria-hidden={true}>
                <Icon name="arrowOffsite" size={16} />
              </span>
            </a>
          ))}
        </div>
      ) : undefined}
    </div>
  );
}

/**
 * One app row. Two flavors:
 * - **Placeholder** (`group.placeholderAuthId` set): zsl-api pre-created and
 *   pre-wired a credential-less placeholder. The row shows the placeholder title
 *   + "Created for you"; "Connect" reconnects that auth in place (no re-wire),
 *   "Select existing" wires a different existing auth instead, and a Zaps
 *   accordion lists the copied Zaps it covers.
 * - **Legacy** (no placeholder): the chosen connection is wired onto every
 *   variant in the compat group (unchanged behavior).
 */
export function ConnectAppRow({
  group,
  isWired,
  onWired,
  disambiguator,
}: Props) {
  const t = useTranslations("connect-apps-modal");
  const { createNotification } = useNotification();
  const [isBusy, setIsBusy] = useState(false);
  // Tracks the wired connection so "Change" can mark it as selected.
  const [selectedAuthId, setSelectedAuthId] = useState<number | undefined>();

  const meta = group.displayMeta;
  const isPlaceholder = group.placeholderAuthId != undefined;
  // Fallback row: placeholder create/wire failed (no `placeholderAuthId`) but
  // zsl-api still reported the covered Zaps. Rendered like a placeholder row
  // (Connect + Select existing + Zaps accordion) except "Connect" opens a
  // new-account auth and there's no "Created for you" placeholder.
  const isFallback = !isPlaceholder && group.coveredZaps != undefined;
  // List by versionless name (the BFF maps this to `versionless_selected_api`,
  // returning all versions), then narrow to this row's compat key below.
  const querySelectedApi = versionless(group.selectedApi);

  const { connections: allConnections, isLoading: isLoadingConnections } =
    useConnections({
      selectedApi: querySelectedApi,
      includeCompatible: true,
      excludeUnusable: true,
      order: "personal_first",
      pageSize: 100,
    });

  // Narrow to auths matching this row's compat key, mirroring the backend so
  // the picker never offers an incompatible major (e.g. a `@2` auth on a `@1`
  // row). Placeholder rows key off `ph:<id>`, so derive the compat key from the
  // row's `selectedApi` rather than `group.key`. Also drop the row's own
  // placeholder — it's the row itself (connected via "Connect"), not a separate
  // "existing" option.
  const rowCompatKey = compatKey(group.selectedApi);
  const connections = useMemo(
    () =>
      allConnections.filter((connection) => {
        if (isPlaceholder && Number(connection.id) === group.placeholderAuthId)
          return false;
        return connection.selectedApi
          ? compatKey(connection.selectedApi) === rowCompatKey
          : true;
      }),
    [allConnections, rowCompatKey, group.placeholderAuthId, isPlaceholder],
  );

  const { wireConnections } = useWireConnections();
  const { refetchAuthentications } = useRefetchAuthentications();
  const createNewConnection = useCreateAppConnection();

  // We only have `implementationMeta` in this context, no `implementation`.
  // useCreateAppConnection's resolution chain falls through to read
  // authType / serviceKey (via implementationMeta.id) / hasUpfrontFields off
  // the meta payload.
  const app = meta
    ? ({ implementationMeta: meta } as unknown as HydratedApp)
    : undefined;
  const appFallbackName = versionless(group.selectedApi);
  const appName = meta?.name || appFallbackName;
  // Legacy rows show the app name (+ version tag on a collision) as the
  // heading; placeholder rows show the placeholder title and put the app name
  // in the subheading.
  const legacyDisplayName = disambiguator
    ? `${appName} (${disambiguator})`
    : appName;
  const headingName = isPlaceholder
    ? group.placeholderTitle || appName
    : legacyDisplayName;
  const appImage =
    meta?.images?.url64X64 || getServiceIconUrl(meta?.id || appFallbackName);

  const selectedConnection = connections.find((c) => c.id === selectedAuthId);
  const selectedLabel =
    selectedConnection?.title ||
    selectedConnection?.label ||
    (selectedAuthId === group.placeholderAuthId
      ? group.placeholderTitle
      : undefined) ||
    t("row.connected");

  // Wire a (different) existing auth onto this row's steps.
  const wire = async (authenticationId: number) => {
    setIsBusy(true);
    try {
      const wirings = group.entries.map((entry) => ({
        selectedApi: entry.selectedApi,
        authenticationId,
        // For a placeholder row, rewire precisely by the auth the steps
        // CURRENTLY carry (the placeholder initially, then whatever was last
        // wired) so swapping only touches this row's steps — never another
        // same-app row's step in the same Zap, and so a second "Change" still
        // matches (the steps no longer carry the original placeholder id).
        // Legacy rows (no placeholder) fall back to compat-key matching.
        ...(group.placeholderAuthId == undefined
          ? {}
          : { fromAuthId: selectedAuthId ?? group.placeholderAuthId }),
        targets: entry.targets,
      }));
      await wireConnections({ wirings });
      setSelectedAuthId(authenticationId);
      onWired(group.key, {
        authId: authenticationId,
        reconnectedPlaceholder: false,
      });
    } catch {
      createNotification({
        message: t("notifications.wire-failed", { appName }),
        type: "error",
      });
    } finally {
      setIsBusy(false);
    }
  };

  // Legacy: open the connect popup for a brand-new account, then wire it.
  const connectNewAndWire = async () => {
    if (!app) return;
    setIsBusy(true);
    const { id } = await createNewConnection(app);
    if (id === undefined || id === null) {
      setIsBusy(false);
      return;
    }
    await wire(Number(id));
    refetchAuthentications();
  };

  // Placeholder: reconnect the existing placeholder auth in place. zsl-api
  // pre-wired it onto the steps, so on a fresh reconnect no re-wire is needed.
  // But if the user previously switched these steps to a DIFFERENT auth (via
  // "Select existing") and then re-selects the placeholder, the steps no longer
  // point at it — rewire them back to the (now reconnected) placeholder so the
  // Zaps actually run on it.
  const reconnectPlaceholder = async () => {
    if (!app || group.placeholderAuthId == undefined) return;
    const placeholderAuthId = group.placeholderAuthId;
    setIsBusy(true);
    try {
      const { id } = await createNewConnection(app, {
        id: placeholderAuthId,
        selectedApi: group.selectedApi,
      });
      if (id === undefined || id === null) return;

      if (
        selectedAuthId !== undefined &&
        selectedAuthId !== placeholderAuthId
      ) {
        await wireConnections({
          wirings: group.entries.map((entry) => ({
            selectedApi: entry.selectedApi,
            authenticationId: placeholderAuthId,
            fromAuthId: selectedAuthId,
            targets: entry.targets,
          })),
        });
      }

      setSelectedAuthId(placeholderAuthId);
      onWired(group.key, {
        authId: placeholderAuthId,
        reconnectedPlaceholder: true,
      });
      refetchAuthentications();
    } catch {
      createNotification({
        message: t("notifications.wire-failed", { appName }),
        type: "error",
      });
    } finally {
      setIsBusy(false);
    }
  };

  // Connection picker. `includeConnectNew` adds the legacy "connect a new
  // account" footer; `includePlaceholder` adds the placeholder as a selectable
  // (re-connectable) item tagged "Created for you" (used by placeholder Change).
  const connectionMenu = (
    label: string,
    variant: "secondary" | "tertiary",
    {
      includeConnectNew,
      includePlaceholder,
    }: { includeConnectNew: boolean; includePlaceholder: boolean },
  ) => (
    <BetaMenuTriggerRoot>
      <Button variant={variant} size="medium" isPending={isBusy}>
        {label}
      </Button>
      <BetaMenuTriggerPopover placement="bottom end">
        <div className={styles.menuPanel}>
          <div className={styles.menuScroll}>
            <BetaMenuTriggerList aria-label={t("select.label", { appName })}>
              {includePlaceholder && group.placeholderAuthId != undefined ? (
                <BetaMenuTriggerItem
                  key="placeholder"
                  textValue={group.placeholderTitle || appName}
                  onAction={() => void reconnectPlaceholder()}
                >
                  <span className={styles.menuItemRow}>
                    <span className={styles.menuItem}>
                      <span className={styles.rowHeading}>
                        <Text
                          type="Body3"
                          tag="span"
                          className={styles.menuItemTitle}
                        >
                          {group.placeholderTitle || appName}
                        </Text>
                        <span className={styles.createdForYouPill}>
                          {t("created-for-you")}
                        </span>
                      </span>
                      <Text type="SmallPrint1" color="TextWeaker" tag="div">
                        {t("select.connection-meta", {
                          shared: "no",
                          count: group.zapCount,
                        })}
                      </Text>
                    </span>
                    {selectedAuthId === group.placeholderAuthId && (
                      <span
                        className={styles.menuItemCheck}
                        role="img"
                        aria-label={t("select.selected")}
                      >
                        <Icon name="formCheck" size={20} />
                      </span>
                    )}
                  </span>
                </BetaMenuTriggerItem>
              ) : undefined}
              {connections.map((connection) => {
                const title =
                  connection.title || connection.label || `#${connection.id}`;
                const isSelected = connection.id === selectedAuthId;
                return (
                  <BetaMenuTriggerItem
                    key={connection.id}
                    textValue={title}
                    onAction={() => void wire(Number(connection.id))}
                  >
                    <span className={styles.menuItemRow}>
                      <span className={styles.menuItem}>
                        <Text
                          type="Body3"
                          tag="div"
                          className={styles.menuItemTitle}
                        >
                          {title}
                        </Text>
                        <Text type="SmallPrint1" color="TextWeaker" tag="div">
                          {t("select.connection-meta", {
                            shared: connection.isShared ? "yes" : "no",
                            count: connection.zapCount ?? 0,
                          })}
                        </Text>
                      </span>
                      {isSelected && (
                        <span
                          className={styles.menuItemCheck}
                          role="img"
                          aria-label={t("select.selected")}
                        >
                          <Icon name="formCheck" size={20} />
                        </span>
                      )}
                    </span>
                  </BetaMenuTriggerItem>
                );
              })}
            </BetaMenuTriggerList>
          </div>
          {includeConnectNew ? (
            // Sticky footer: stays visible even when the list scrolls.
            <div className={styles.menuFooter}>
              <Button
                variant="primary"
                size="medium"
                isFullWidth={true}
                iconPosition="start"
                onPress={connectNewAndWire}
              >
                <Icon name="formAdd" aria-hidden={true} />
                {t("select.connect-new")}
              </Button>
            </div>
          ) : undefined}
        </div>
      </BetaMenuTriggerPopover>
    </BetaMenuTriggerRoot>
  );

  const action = (() => {
    if (isWired) {
      return (
        <div className={styles.wiredState}>
          <TruncatedText
            value={selectedLabel}
            type="SmallPrint1"
            color="TextDefault"
            className={styles.connectionName}
          />
          {isPlaceholder ? (
            // Change always available: switch to an existing auth or re-pick
            // the placeholder. No "connect a new account" — the placeholder
            // already stands in for that.
            connectionMenu(t("buttons.change"), "tertiary", {
              includeConnectNew: false,
              includePlaceholder: true,
            })
          ) : connections.length > 0 ? (
            connectionMenu(t("buttons.change"), "tertiary", {
              includeConnectNew: true,
              includePlaceholder: false,
            })
          ) : (
            <Button
              variant="tertiary"
              size="medium"
              onPress={connectNewAndWire}
              isPending={isBusy}
            >
              {t("buttons.change")}
            </Button>
          )}
        </div>
      );
    }

    // Pending trigger while loading, so the label doesn't flip Select↔Connect.
    if (isLoadingConnections) {
      return (
        <Button variant="secondary" size="medium" isPending={true}>
          {isPlaceholder || isFallback
            ? t("buttons.connect")
            : t("select.placeholder")}
        </Button>
      );
    }

    if (isPlaceholder || isFallback) {
      // Always offer "Connect"; add "Select existing" when the account already
      // has a compatible connection. Placeholder rows reconnect the pre-wired
      // placeholder; fallback rows open a new-account auth (the placeholder
      // failed), wiring the result onto this entry's steps.
      return (
        <>
          {connections.length > 0
            ? connectionMenu(t("select.select-existing"), "tertiary", {
                includeConnectNew: false,
                includePlaceholder: false,
              })
            : undefined}
          <Button
            variant="secondary"
            size="medium"
            onPress={isPlaceholder ? reconnectPlaceholder : connectNewAndWire}
            isPending={isBusy}
            isDisabled={!app}
          >
            {t("buttons.connect")}
          </Button>
        </>
      );
    }

    if (connections.length > 0) {
      return connectionMenu(t("select.placeholder"), "secondary", {
        includeConnectNew: true,
        includePlaceholder: false,
      });
    }

    return (
      <Button
        variant="secondary"
        size="medium"
        onPress={connectNewAndWire}
        isPending={isBusy}
        isDisabled={!app}
      >
        {t("buttons.connect")}
      </Button>
    );
  })();

  return (
    <div className={styles.appBlock}>
      <div className={styles.row}>
        <div className={styles.rowInfo}>
          <span className={styles.rowIcon}>
            <ServiceIcon serviceName={appName} size="medium" src={appImage} />
          </span>
          <div className={styles.rowText}>
            {isPlaceholder ? (
              <>
                <span className={styles.rowHeading}>
                  <TruncatedText
                    value={headingName}
                    type="Body3"
                    color="TextDefault"
                    className={styles.appName}
                  />
                  <span className={styles.createdForYouPill}>
                    {t("created-for-you")}
                  </span>
                </span>
                <Text type="SmallPrint1" color="TextWeaker" tag="div">
                  {appName}
                </Text>
              </>
            ) : isFallback ? (
              // No connection/placeholder exists yet — show just the app name as
              // the primary header (same styling as the placeholder title), no
              // subheading. A single line, vertically centered by the row.
              <TruncatedText
                value={headingName}
                type="Body3"
                color="TextDefault"
                className={styles.appName}
              />
            ) : (
              <>
                <TruncatedText
                  value={headingName}
                  type="Body3"
                  color="TextDefault"
                  className={styles.appName}
                />
                <Text type="SmallPrint1" color="TextWeaker" tag="div">
                  {t("row.used-in-assets", { count: group.zapCount })}
                </Text>
              </>
            )}
          </div>
        </div>
        <div className={styles.rowAction}>{action}</div>
      </div>
      {group.coveredZaps?.length ? (
        <ZapsAccordion zaps={group.coveredZaps} />
      ) : undefined}
    </div>
  );
}
